'use client';

import { useEffect, useRef } from 'react';
import s from './Careers.module.css';

/* Careers — content lives in the const arrays below (copy from the approved
   Careers design). Layout/motion are page-local; see Careers.module.css.
   Keep JOBS in sync with the open-positions folder — add / remove roles here. */

const HERO = {
  titleLead: 'Locobuzz.',
  titleTail: 'For people, by people.',
  sub: 'We build customer experience software. It would be strange not to start with our own.',
};

const APPLY_EMAIL = 'careers@locobuzz.com';

// Hero collage — positions are in the 1440×730 stage coordinate space.
const PHOTOS = [
  { n: '01', alt: 'Colleagues in the office', left: 25, top: 128, w: 151, h: 187 },
  { n: '02', alt: '10 years of Locobuzz celebration', left: 181, top: 57, w: 151, h: 187 },
  { n: '03', alt: 'Cricket tournament trophy', left: 24, top: 320, w: 151, h: 187 },
  { n: '04', alt: 'Team hanging out', left: 183, top: 250, w: 151, h: 187 },
  { n: '05', alt: 'Team outing at Prithvi Theatre', left: 341, top: 135, w: 145, h: 179 },
  { n: '06', alt: 'Packing festival hampers', left: 495, top: 71, w: 145, h: 179 },
  { n: '07', alt: 'Colleagues at the office desk', left: 652, top: 101, w: 143, h: 177 },
  { n: '08', alt: 'Cake cutting in the office', left: 806, top: 61, w: 153, h: 189 },
  { n: '09', alt: 'Awards evening', left: 969, top: 128, w: 145, h: 179 },
  { n: '10', alt: 'Town hall with the team', left: 1125, top: 55, w: 145, h: 179 },
  { n: '11', alt: 'Interns onboarding session', left: 1125, top: 255, w: 145, h: 179 },
  { n: '12', alt: 'Colleagues taking a selfie', left: 1277, top: 122, w: 143, h: 177 },
  { n: '13', alt: 'Team after a workshop', left: 1277, top: 322, w: 143, h: 177 },
];

const FACTS = [
  { value: '170+', label: 'People' },
  { value: '5', label: 'Offices' },
  { value: 'ROLES', label: 'Roles open now' },
  { value: '2015', label: 'Founded' },
];

const WORK_CARDS = [
  { tag: 'All hands', title: 'Learning never stops', alt: 'The team seated together at an all-hands session' },
  { tag: 'Building', title: 'Innovative spirit', alt: 'Hands-on session in the office' },
  { tag: 'Launch', title: 'Work that ships', alt: 'Colleagues posing for a selfie in the office' },
  { tag: 'Cricket', title: 'Friday cricket and chai', alt: 'Locobuzz Cricket League team celebrating' },
  { tag: 'Mentoring', title: 'Grow past your title', alt: 'Colleagues in the office' },
  { tag: 'Standup', title: 'Say what you do', alt: 'Team behind the 10 years of Locobuzz frame' },
];

const PERKS = [
  { n: '01', title: 'Open & ergonomic workspaces', body: 'Open workspace architecture designed to encourage collaboration while supporting employee comfort and ergonomics.' },
  { n: '02', title: 'Learning & upskilling', body: 'Dedicated learning budgets to continuously build new skills, explore areas of interest, and grow professionally throughout the year.' },
  { n: '03', title: 'Internal career mobility', body: 'Opportunities to explore internal transfers and move into roles aligned with your aspirations, interests, and growth goals.' },
  { n: '04', title: 'On-site gym facility', body: 'Access to a gym within the building, making it easier to prioritise fitness and well-being.' },
  { n: '05', title: 'Sports & recreation', body: 'A sports-friendly campus with football and cricket turfs, pickleball courts, gaming zones, and other recreational spaces.' },
  { n: '06', title: 'Team outings & offsites', body: 'Regular outings, offsites, evening socials, and informal team experiences that encourage connection beyond work.' },
  { n: '07', title: 'Employee insurance', body: 'Insurance coverage that gives employees added financial protection and peace of mind.' },
];

const CITIES = [
  { name: 'Mumbai', src: 'mumbai', w: 'min(200px, 14vw)', rot: -8, overlap: '0px', z: 1 },
  { name: 'Hyderabad', src: 'hyderabad', w: 'min(231px, 16vw)', rot: -4, overlap: 'max(-40px, -3vw)', z: 2 },
  { name: 'Delhi', src: 'delhi', w: 'min(230px, 16vw)', rot: 2, overlap: 'max(-40px, -3vw)', z: 4 },
  { name: 'Bangalore', src: 'bangalore', w: 'min(248px, 17vw)', rot: -5, overlap: 'max(-40px, -3vw)', z: 3 },
  { name: 'Singapore', src: 'singapore', w: 'min(182px, 13vw)', rot: 4, overlap: 'max(-40px, -3vw)', z: 2 },
];

const JOBS = [
  {
    slug: 'enterprise-sales-manager',
    title: 'Enterprise Sales Manager',
    dept: 'Sales',
    location: 'Mumbai / Gurugram / Bengaluru',
    jdUrl: 'https://drive.google.com/file/d/1WMICvQW_vQdOGuui7iEkqKfpMishMUP3/view?usp=drivesdk',
  },
  {
    slug: 'sdr',
    title: 'Sales Development Representative (SDR)',
    dept: 'Growth',
    location: 'Mumbai / Gurugram / Bengaluru',
    experience: '1–3 Years',
    jdUrl: 'https://drive.google.com/file/d/1rvTDm6-uj-BMvTUsNC7tT5kqV31AMu-j/view?usp=drivesdk',
  },
  {
    slug: 'customer-success-specialist',
    title: 'Customer Success Specialist',
    dept: 'Customer Success',
    location: 'Mumbai / Gurugram / Bengaluru',
    jdUrl: 'https://drive.google.com/file/d/108jYBKJ_Leznuzd18KxRHLfj4EDMpphb/view?usp=drivesdk',
  },
  {
    slug: 'customer-support-executive',
    title: 'Customer Support Executive',
    dept: 'Customer Support',
    location: 'Mumbai',
    experience: '3+ Years',
    jdUrl: 'https://drive.google.com/file/d/1G0a-w95kJGjaYlSLS-wqZymwjaQkk6cV/view?usp=drivesdk',
  },
  {
    slug: 'orm-executive',
    title: 'ORM Executive',
    dept: 'ORM Team',
    location: 'Mumbai (Andheri)',
    experience: '1+ Years',
    jdUrl: 'https://drive.google.com/file/d/1sGuKtxEJQqNEjWZlUMtNRVMofmDO-c6r/view?usp=drivesdk',
  },
  {
    slug: 'manager-orm',
    title: 'Manager – ORM',
    dept: 'ORM Team',
    location: 'Mumbai (Onsite)',
    experience: '4+ Years',
    jdUrl: 'https://drive.google.com/file/d/1ujT1OfkuWoO51jTs5L72fgM209dQ8ow3/view?usp=drivesdk',
  },
  {
    slug: 'ux-designer',
    title: 'UX Designer',
    dept: 'Product & Design',
    location: 'Mumbai',
    experience: '4–7 Years',
    jdUrl: 'https://drive.google.com/file/d/1BiPhaPGyr7eyOj82_nWttP_7yGjnEVac/view?usp=drivesdk',
  },
  {
    slug: 'legal-executive',
    title: 'Legal Executive',
    dept: 'Legal',
    location: 'Mumbai',
    experience: '1–3 Years',
    jdUrl: 'https://drive.google.com/file/d/1TjHNwMAZeQ-YOxigq-nBWIjJYjO3mO1B/view?usp=drivesdk',
  },
];

const ROLE_COUNT = JOBS.length;

/* Job-description PDFs: drop `<slug>.pdf` (slug = the `slug` field on each job) into
   public/careers/jd/. page.js detects which PDFs exist at build time; roles without
   one fall back to jdUrl (the Drive copy). */
export default function CareersClient({ pdfSlugs = [] }) {
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const citiesRef = useRef(null);

  // Scale the fixed 1440×730 collage stage to the available width/height.
  useEffect(() => {
    const w = wrapRef.current;
    const st = stageRef.current;
    if (!w || !st) return undefined;
    const fit = () => {
      if (window.innerWidth <= 900) { st.style.transform = ''; w.style.height = ''; return; }
      const top = w.getBoundingClientRect().top + window.scrollY;
      const avail = Math.max(420, window.innerHeight - top - 24);
      const scale = Math.min(1, w.clientWidth / 1440, avail / 730);
      st.style.transform = `translateX(-50%) scale(${scale})`;
      w.style.height = `${Math.round(730 * scale)}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(w);
    window.addEventListener('resize', fit);
    return () => { ro.disconnect(); window.removeEventListener('resize', fit); };
  }, []);

  // Cities pop in once when scrolled into view.
  useEffect(() => {
    const root = citiesRef.current;
    if (!root) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tiles = Array.from(root.querySelectorAll('[data-city]'));
    if (!reduce) tiles.forEach((el) => { el.style.opacity = '0'; });
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      tiles.forEach((el, i) => {
        el.style.opacity = '';
        if (!reduce) { el.classList.add(s.cityIn); el.style.animationDelay = `${i * 95}ms`; }
      });
      io.disconnect();
    }, { threshold: 0.2 });
    io.observe(root);
    return () => io.disconnect();
  }, []);

  return (
    <div className={s.root}>
        <section className={s.hero}>
          <div className={s.heroGlow} />
          <div ref={wrapRef} className={s.heroWrap}>
            <div ref={stageRef} className={s.stage}>
              {PHOTOS.map((p, i) => {
                const left = p.left + p.w / 2 < 720;
                return (
                  <div
                    key={p.n}
                    className={`${s.photo} ${left ? s.photoL : s.photoR}`}
                    style={{ left: p.left, top: p.top, width: p.w, animationDelay: `${Math.round(Math.abs(p.left + p.w / 2 - 720) * 0.42)}ms` }}
                  >
                    <div className={s.float} style={{ animationDelay: `${(i * 0.45).toFixed(2)}s` }}>
                      <img className={s.photoImg} src={`/careers/team-${p.n}.webp`} alt={p.alt} width={p.w} height={p.h} style={{ height: p.h }} loading={i < 5 ? 'eager' : 'lazy'} decoding="async" />
                    </div>
                  </div>
                );
              })}
              <div className={s.heroText}>
                <h1 className={s.heroTitle}>{HERO.titleLead}<br />{HERO.titleTail}</h1>
                <p className={s.heroSub}>{HERO.sub}</p>
                <div className={s.heroBtns}>
                  <a href="#roles" className={s.btnPrimary}><span>View open roles</span>↓</a>
                  <a href={`mailto:${APPLY_EMAIL}?subject=Open application`} className={s.btnGhost}>Send an open application</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={s.facts}>
          <div className={s.wrap}>
            <div className={s.factsBand}>
              <div className={s.factsGrid} />
              <div className={s.factsRow}>
                {FACTS.map((f) => (
                  <div key={f.label} className={s.fact}>
                    <div className={s.factValue}>{f.value === 'ROLES' ? ROLE_COUNT : f.value}</div>
                    <div className={s.factLabel}>{f.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className={s.how}>
          <div className={s.wrap}>
            <div className={s.eyebrow}>How we work</div>
            <h2 className={s.howH2}>Six things, no speeches.</h2>
            <div className={s.cards}>
              {WORK_CARDS.map((c, i) => (
                <div key={c.title} className={s.card}>
                  <img className={s.cardImg} src={`/careers/work-${String(i + 1).padStart(2, '0')}.webp`} alt={c.alt} loading="lazy" decoding="async" />
                  <div className={s.cardShade} />
                  <div className={s.cardTag}>{c.tag}</div>
                  <div className={s.cardTitle}>{c.title}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={s.life}>
          <div className={s.wrap}>
            <div className={s.eyebrow}>The rest of it</div>
            <h2 className={`${s.h2} ${s.lifeH2}`}>Life at Locobuzz.</h2>
            <div className={s.perks}>
              {PERKS.map((p) => (
                <div key={p.n} className={s.perk}>
                  <div className={s.perkN}>{p.n}</div>
                  <div className={s.perkTitle}>{p.title}</div>
                  <div className={s.perkBody}>{p.body}</div>
                </div>
              ))}
              <div className={s.perkPromise}>
                <div>Seven small things. <span>One promise:</span> you are treated like an adult.</div>
              </div>
            </div>
          </div>
        </section>

        <section ref={citiesRef} className={s.cities}>
          <div className={s.wrap}>
            <div className={s.citiesBox}>
              <h2 className={s.citiesH2}>Five cities, one team.</h2>
              <p className={s.citiesSub}>Pick the city that suits you. Most teams work across all five anyway.</p>
              <div className={s.cityRow}>
                {CITIES.map((c) => (
                  <div key={c.name} data-city="1" className={s.city} style={{ marginLeft: c.overlap, zIndex: c.z }}>
                    <img className={s.cityImg} src={`/careers/${c.src}.webp`} alt={`${c.name} office`} title={c.name} loading="lazy" decoding="async" style={{ width: c.w, transform: `rotate(${c.rot}deg)` }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="roles" className={s.roles}>
          <div className={s.wrap}>
            <div className={s.rolesHead}>
              <div>
                <div className={s.eyebrow}>Open roles</div>
                <h2 className={s.h2}>Come build it.</h2>
              </div>
              <div className={s.rolesCount}>{ROLE_COUNT} roles open across sales, customer success, ORM, design and legal.</div>
            </div>
            <div className={s.roleGrid}>
              {JOBS.map((job) => (
                <div key={job.slug} className={s.role}>
                  <div className={s.roleMain}>
                    <div className={s.chips}>
                      <span className={s.dept}>{job.dept}</span>
                      <span className={s.chip}>{job.location}</span>
                    </div>
                    <div className={s.roleTitle}>{job.title}</div>
                    <div className={s.roleMeta}>
                      {job.experience && `${job.experience} · `}
                      <a
                        href={pdfSlugs.includes(job.slug) ? `/careers/jd/${job.slug}.pdf` : job.jdUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View job description
                      </a>
                    </div>
                  </div>
                  <a className={s.apply} href={`mailto:${APPLY_EMAIL}?subject=${encodeURIComponent(`Application — ${job.title}`)}`}>Apply Job</a>
                </div>
              ))}
            </div>
            <p className={s.openApp}>
              Don’t see your role? Write to <a href={`mailto:${APPLY_EMAIL}`}>{APPLY_EMAIL}</a>.
            </p>
          </div>
        </section>

        <section className={s.cta}>
          <div className={s.wrap}>
            <div className={s.ctaBox}>
              <div>
                <h2 className={s.ctaH2}>Come build the thing you’d want used on you.</h2>
                <a href="#roles" className={s.ctaBtn}><span>View open roles</span></a>
              </div>
              <img className={s.ctaImg} src="/careers/cta.webp" alt="A hand placing the final brick on a stack" width="217" height="209" loading="lazy" decoding="async" />
            </div>
          </div>
        </section>
    </div>
  );
}
